import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { useCart, computeUnitPrice } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MenuCard = ({ item }) => {
  const { addToCart, loading } = useCart();
  const [showOptionsDialog, setShowOptionsDialog] = useState(false);
  const [addShrimp, setAddShrimp] = useState(false);
  const [kidsPortion, setKidsPortion] = useState(false);
  const [withFormule, setWithFormule] = useState(false);
  const [formuleDrinkId, setFormuleDrinkId] = useState('');
  const [formuleDessertId, setFormuleDessertId] = useState('');
  const [formuleOptions, setFormuleOptions] = useState({ drinks: [], desserts: [] });

  const hasAnyOption = item.has_shrimp_addon || item.has_kids_option || item.has_formule_option;

  useEffect(() => {
    if (showOptionsDialog && item.has_formule_option && formuleOptions.drinks.length === 0) {
      axios.get(`${API}/menu/formule-options`).then(res => setFormuleOptions(res.data)).catch(() => {});
    }
  }, [showOptionsDialog, item.has_formule_option, formuleOptions.drinks.length]);

  const resetOptionsForm = () => {
    setAddShrimp(false);
    setKidsPortion(false);
    setWithFormule(false);
    setFormuleDrinkId('');
    setFormuleDessertId('');
  };

  const buildOptions = () => {
    const opts = {};
    if (addShrimp) opts.add_shrimp = true;
    if (kidsPortion) opts.kids_portion = true;
    if (withFormule && formuleDrinkId && formuleDessertId) {
      const drink = formuleOptions.drinks.find(d => d.item_id === formuleDrinkId);
      const dessert = formuleOptions.desserts.find(d => d.item_id === formuleDessertId);
      opts.formule = {
        drink_id: drink.item_id,
        drink_name: drink.name,
        dessert_id: dessert.item_id,
        dessert_name: dessert.name,
      };
    }
    return Object.keys(opts).length ? opts : null;
  };

  const dialogUnitPrice = computeUnitPrice(item.price, buildOptions());
  const formuleIncomplete = withFormule && (!formuleDrinkId || !formuleDessertId);

  const handleAddClick = () => {
    if (hasAnyOption) {
      setShowOptionsDialog(true);
    } else {
      doAdd(null);
    }
  };

  const doAdd = async (opts) => {
    const ok = await addToCart(item, 1, opts);
    if (ok) toast.success(`${item.name} ajouté au panier`);
    else toast.error("Erreur lors de l'ajout au panier");
  };

  const handleConfirmOptions = async () => {
    if (formuleIncomplete) {
      toast.error('Veuillez choisir une boisson et un dessert pour la formule');
      return;
    }
    await doAdd(buildOptions());
    setShowOptionsDialog(false);
    resetOptionsForm();
  };

  return (
    <>
      <div
        className="group bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-stone-100"
        data-testid={`menu-card-${item.item_id}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={item.image_url}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800'; }}
          />
          {item.is_featured && (
            <Badge className="absolute top-3 left-3 bg-primary text-white">Coup de coeur</Badge>
          )}
          {!item.is_available && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="bg-white rounded-full px-4 py-2">
                <span className="text-sm font-semibold text-destructive">Indisponible pour le moment</span>
              </div>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-serif text-lg font-semibold text-foreground line-clamp-1">{item.name}</h3>
            <span className="text-lg font-bold text-primary whitespace-nowrap ml-2">
              {item.price.toFixed(2)} €
            </span>
          </div>

          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{item.description}</p>

          {hasAnyOption && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {item.has_shrimp_addon && (
                <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full font-medium">
                  + Crevettes (+1€)
                </span>
              )}
              {item.has_formule_option && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                  Formule (+4€)
                </span>
              )}
              {item.has_kids_option && (
                <span className="text-xs bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full font-medium">
                  Portion enfant (-4€)
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Clock className="h-3.5 w-3.5" />
                <span>{item.preparation_time} min</span>
              </div>
              {item.allergens && item.allergens.length > 0 && (
                <div className="flex items-center space-x-1">
                  <AlertCircle className="h-3.5 w-3.5 text-warning" />
                  <span>{item.allergens.length}</span>
                </div>
              )}
            </div>

            <Button
              size="sm"
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={handleAddClick}
              disabled={loading || !item.is_available}
              data-testid={`add-to-cart-${item.item_id}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter
            </Button>
          </div>

          {item.allergens && item.allergens.length > 0 && (
            <div className="mt-3 pt-3 border-t border-stone-100">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Allergènes:</span> {item.allergens.join(', ')}
              </p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showOptionsDialog} onOpenChange={(o) => { setShowOptionsDialog(o); if (!o) resetOptionsForm(); }}>
        <DialogContent data-testid="options-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Personnaliser {item.name}</DialogTitle>
            <DialogDescription>Prix de base : {item.price.toFixed(2)} €</DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {item.has_shrimp_addon && (
              <label className="flex items-start space-x-3 p-4 rounded-xl border border-border hover:border-accent cursor-pointer transition-colors">
                <Checkbox
                  checked={addShrimp}
                  onCheckedChange={setAddShrimp}
                  data-testid="option-add-shrimp"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="font-medium">Ajouter des crevettes 🦐</p>
                  <p className="text-sm text-muted-foreground">Supplément +1,00 €</p>
                </div>
              </label>
            )}

            {item.has_kids_option && (
              <label className="flex items-start space-x-3 p-4 rounded-xl border border-border hover:border-accent cursor-pointer transition-colors">
                <Checkbox
                  checked={kidsPortion}
                  onCheckedChange={setKidsPortion}
                  data-testid="option-kids-portion"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="font-medium">Portion enfant 🧒</p>
                  <p className="text-sm text-muted-foreground">Réduction -4,00 €</p>
                </div>
              </label>
            )}

            {item.has_formule_option && (
              <div className="rounded-xl border border-border overflow-hidden">
                <label className="flex items-start space-x-3 p-4 cursor-pointer">
                  <Checkbox
                    checked={withFormule}
                    onCheckedChange={setWithFormule}
                    data-testid="option-formule"
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <p className="font-medium">Formule complète 🍹🍰</p>
                    <p className="text-sm text-muted-foreground">1 boisson + 1 dessert — supplément +4,00 €</p>
                  </div>
                </label>

                {withFormule && (
                  <div className="px-4 pb-4 space-y-4 bg-stone-50">
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Boisson</Label>
                      <RadioGroup value={formuleDrinkId} onValueChange={setFormuleDrinkId} className="space-y-2">
                        {formuleOptions.drinks.map(d => (
                          <label
                            key={d.item_id}
                            className={`flex items-center space-x-3 p-2 rounded-lg border cursor-pointer ${
                              formuleDrinkId === d.item_id ? 'border-primary bg-primary/5' : 'border-border bg-white'
                            }`}
                          >
                            <RadioGroupItem value={d.item_id} data-testid={`formule-drink-${d.item_id}`} />
                            <span className="text-sm">{d.name}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                    <div>
                      <Label className="text-sm font-medium mb-2 block">Dessert</Label>
                      <RadioGroup value={formuleDessertId} onValueChange={setFormuleDessertId} className="space-y-2">
                        {formuleOptions.desserts.map(d => (
                          <label
                            key={d.item_id}
                            className={`flex items-center space-x-3 p-2 rounded-lg border cursor-pointer ${
                              formuleDessertId === d.item_id ? 'border-primary bg-primary/5' : 'border-border bg-white'
                            }`}
                          >
                            <RadioGroupItem value={d.item_id} data-testid={`formule-dessert-${d.item_id}`} />
                            <span className="text-sm">{d.name}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div>
              <p className="text-xs text-muted-foreground">Prix final</p>
              <p className="text-2xl font-bold text-primary" data-testid="options-dialog-price">
                {dialogUnitPrice.toFixed(2)} €
              </p>
            </div>
            <Button
              onClick={handleConfirmOptions}
              className="rounded-full"
              disabled={loading || formuleIncomplete}
              data-testid="confirm-options"
            >
              Ajouter au panier
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MenuCard;
